import {
    IStartTesting,
    IStartTestingResponse,
    ISubmitQuestionTesting,
    ISubmitQuestionTestingResponse,
    ISubmitTesting,
    IResultTesting,
    ISurveyUser,
} from '@/features/landing/interface';
import api from './api';
import { IGetListAssessmentParams } from '@/features/student/Assessments/interface';

export interface ISkillBreakdown {
    vocabulary: number;
    grammar: number;
    reading: number;
    listening: number;
    speaking: number;
    writing: number;
}

export interface IAssessmentDetails {
    totalQuestions: number;
    correctAnswers: number;
    timeSpent: string;
    skillBreakdown: ISkillBreakdown;
    activities?: IActivityDetail[];
}

export interface IActivityDetail {
    activityId: number;
    activityType: string;
    title: string;
    score: number;
    isPassed: boolean;
    timeSpent: number; // in minutes
    attempts: number;
    feedback?: string;
    completedAt?: string;
}

export interface ILessonInfo {
    description?: string;
    difficulty?: string;
    estimatedDuration?: number;
    xp?: number;
    learningObjectives?: string[];
    grammarPoints?: string[];
    vocabPoints?: string[];
}

export interface IHistoryTestResponse {
    id: number;
    title?: string;
    assessmentType: string;
    score: number;
    date: string | null;
    duration: string;
    level: string;
    strengths: string[];
    weaknesses: string[];
    recommendations: string[];
    details: IAssessmentDetails;
    lessonInfo?: ILessonInfo;
}

export interface ICreateTestingResponse {
    message: string;
    data: {
        lesson_id: number
    };
    assessmentType: string;
}

class TestingService {
    async createSurvey(data: ISurveyUser): Promise<ISurveyUser> {
        const response = await api.post('/assessment/survey-user/', data);
        return response.data;
    }

    async startTesting(data: IStartTesting): Promise<IStartTestingResponse> {
        const response = await api.post('/assessment/start/', data, {
            timeout: 1000000,
        });
        return response.data;
    }

    async submitTesting(data: ISubmitTesting): Promise<IResultTesting> {
        const response = await api.post('/assessment/submit/', data, {
            timeout: 1000000,
        });
        return response.data;
    }

    async submitQuestion(data: ISubmitQuestionTesting): Promise<ISubmitQuestionTestingResponse> {
        const response = await api.post('/assessment/submit-question/', data, {
            timeout: 1000000,
        });
        return response.data;
    }

    async getResultTesting(token: string): Promise<IResultTesting> {
        const response = await api.get(`/assessment/result/${token}/`);
        return response.data;
    }

    async getListHistoryTest(filter?: IGetListAssessmentParams): Promise<{ message: string; data: IHistoryTestResponse[] }> {
        const response = await api.get(`/syllabus/assessment/`, { params: filter });
        return response.data;
    }

    async getDetailsHistoryTest(lessonId: string | number): Promise<{ message: string; data: IHistoryTestResponse }> {
        const response = await api.get(`/syllabus/assessment/${lessonId}/`);
        return response.data;
    }

    async createTestingLesson(testingType: 'full_test' | 'quick_test'): Promise<ICreateTestingResponse> {
        const response = await api.post(`/syllabus/assessment/`, {
            type: testingType
        });
        return response.data;
    }
}

export default new TestingService();
